import { initLocale } from "csi/localeLoader";
import { decomposeHash, LOCATION_SESSION_URL, setLocationHashEncodingEnabled } from "csi/utility/routerUtility";
import comm from "csi/comm";
import GetRequest from "csi/comm/GetRequest";
import csi from "csi/main";
import { rootCache } from "csi/state/Cache";
import { featureTogglesStore, sessionStore } from "csi/state/rootStore";
import { getToken } from "csi/keycloak/KeycloakAdapter";
import LanguageUtil from "csi/widget/LanguageUtil";
import xml from "csi/xml";
import config from "dojo/_base/config";
import crews from "csi/access/crews";
import { setModuleNames } from "csi/_base/modules";
import reflection from "csi/reflection";
import { PRINT_ORIENTATION, PRINT_SIZE } from "src/c8-platform/type/print";
import sessionConstants from "csi/sessionConstants";
import Search from "csi/xml/Search";
import configPreferences from "csi/config/preferences";
import topic from "dojo/topic";
import xhr from "dojo/request/xhr";
import JsonResponse from "csi/comm/JsonResponse";
import { C8Node, C8NodeType, cnl } from "csi/typings";
import * as openTelemetryService from "./openTelemetryService";
import csiHeading from "csi/heading/_base";

/**
 * initialize session and globals
 */
export async function init(): Promise<void> {
	openTelemetryService.init();

	const request = new GetRequest("DataSource", "GetWebClientSession");
	const params = decomposeHash();

	if (!!getLocationSearchParameterValue(params.array)) {
		// use our own locale code
		request.addParameter("SelectedLocale", csi.getCurrentLocale());
	}

	if (params[LOCATION_SESSION_URL]) {
		request.addParameter(LOCATION_SESSION_URL, params[LOCATION_SESSION_URL]);
	}

	return getToken()
		.then(() => sessionStore.submitRequest(request))
		.then(_initSession)
		.then(_initLocale)
		.then(_initPinyin);
}

/**
 * query for new set of playable roles; however, this should never be called when a role is deleted
 * or no longer playable - instead the page should be refreshed.
 */
export async function initializeRoles(): Promise<void> {
	// FIX: would be nice to have a simple call to server here rather than having to specify detailed query
	const { currentUser } = sessionStore;
	const roleSearch = new Search()
		.byType("Role");
	roleSearch.getQuery()
		.Attr("ManagePreferences", xml.OP_EQUALS, true);
	roleSearch.addDependencyPaths(["Child:Preferences"]);

	if (!sessionConstants.configEditor) {
		roleSearch.setLimitPath(currentUser.$URL, "Parent:AssignedUsers/Child:Role");
		roleSearch.setLimitPath(currentUser.$URL, "Parent:__Members__/Child:__Parent__");
		roleSearch.getSortQuery()
			.SortByNode(xml.PROP_NAME);
	}

	const response = await roleSearch.execute();
	rootCache.cacheResponse(response);
	const nodes = response.getResultNodes();
	sessionStore.setPlayableRoles(nodes);
	topic.publish("/csi/roles/change");
}

function _initSession(response: JsonResponse): void {
	if (!response || !response.isSuccessful()) {
		throw new Error("GetWebClientSession failed");
	}

	const session = response.getNodeOfType("_CS_Session_");
	const user = response.getNodeOfType(xml.TYPE_USER);

	const userUrl = user.$URL;
	const userDetails = rootCache.get(user.Details);

	const localeSetting = response.getNodeOfType("LocaleSettings");
	const licenseSettings = response.getNodeOfType("LicenseSettings");
	const roleNames = [];
	let bTeam = user.IsOnlineTeamManager;
	let bAdmin = false;
	let bEditor = false;
	let bViewer = false;
	let bDebugViewer = false;

	// determine user permissions
	response.getNodes()
		.forEach((node) => {
			if ((node.$Type == xml.TYPE_GROUP) || (node.UsedByACL && (node.$Type == xml.TYPE_ROLE))) {
				const urlRole = node.$URL;

				if (urlRole == xml.ROLE_ADMIN) {
					bAdmin = true;
				} else if (urlRole == xml.ROLE_CONFIG_EDITOR) {
					bEditor = true;
				} else if (urlRole == xml.ROLE_CONFIG_VIEWER) {
					bViewer = true;
				} else if (urlRole == xml.ROLE_TEAM_MANAGER) {
					bTeam = true;
				} else if (urlRole == xml.DEBUG_VIEWER) {
					bDebugViewer = true;
				}
				roleNames.push(node.$Name);
			}
		});

	// Feature toggles
	Object.assign(featureTogglesStore, session.FeatureToggles);
	const userLicense = licenseSettings.Config.map(xml.enumGetText);

	const configEditor = bAdmin || bEditor;
	const configViewer = configEditor || bViewer;
	const debugViewer = configEditor || bDebugViewer;
	const setupViewer = configViewer || bTeam;

	const dev = session.DevelopmentConfig && !!config.isDebug;
	if (dev) {
		console.log("Development environment: Site_Merged files are disabled.");
	}

	comm.setAsAdmin(bAdmin);
	crews.setAsAdmin(bAdmin);
	crews.setGlobalRoles(roleNames);
	setModuleNames(session.ModuleNames);

	const { labelTemplateTypes } = session;

	// set portal variables for portal users
	const userCrew: C8Node = rootCache.get(user.__Parent__);
	const userHost: C8Node = userCrew ? rootCache.get(userCrew.__Parent__) : null;
	let supplier: C8Node = rootCache.get(userDetails.HomeSupplier);
	let isAgent: boolean;
	let roleName: string = "";
	let roleUrl: cnl;
	let userCompanyUrl: cnl;
	let userCompanyInfoUrl: cnl;
	let userSupplierUrl: cnl;
	let userType: C8NodeType = "";

	if (userHost) {
		userCompanyInfoUrl = userCrew.CompanyInfo;
		userCompanyUrl = userHost.$URL;
		userType = userHost.$Type;

		if (userHost.$Type == "Supplier") {
			supplier = userHost;
		}
	}

	// set supplier variables
	if (supplier) {
		isAgent = supplier.IsAgent && !!supplier.Suppliers.length;
		userSupplierUrl = supplier.$URL;
	}

	// set site defaults
	const companyInfo = response.getNode(reflection.URL_COMPANY);
	const siteLocale = companyInfo.DefaultLocale;

	const excludedFileExtensions = (companyInfo.ExcludedFileExtensions ?? "").replace(/\s/g, "")
		.split(",");
	const exemptedFileExtensions = (companyInfo.ExemptedFileExtensions ?? "").replace(/\s/g, "")
		.split(",");
	const maxResultSize = companyInfo.MaxResultSize ?? 0;
	const showUserNameOnHeader = companyInfo.ShowUserNameOnHeader ?? false;
	const siteMaxCompositionColumns = Math.max(companyInfo.MaxCompositionColumns ?? 0, 1);

	// set siteMaxExportSize
	let siteMaxExportSize = companyInfo.MaxTableSizeViewPDF ?? 0;
	siteMaxExportSize = (siteMaxExportSize > 0) ? siteMaxExportSize : maxResultSize;

	// set sitePaperOrientation
	let sitePaperOrientation = companyInfo.DefaultPaperOrientation ?? "";
	sitePaperOrientation = sitePaperOrientation ? xml.enumGetText(sitePaperOrientation) : PRINT_ORIENTATION.LANDSCAPE;

	// set sitePaperSize
	let sitePaperSize = companyInfo.DefaultPaperSize ?? "";
	sitePaperSize = sitePaperSize ? xml.enumGetText(sitePaperSize) : PRINT_SIZE.LETTER;

	// set validIllustratorExtensions
	const validIllustratorExtensions = companyInfo.SupportedIllustratorExtensions ? companyInfo.SupportedIllustratorExtensions.replace(/[;\s]/g, ",")
		.split(",") : [];

	// Set file types supported for proofing
	const validatedProofingSupportedFileTypes = session.ProofingSupportedFileTypes.replace(/\s/g, "");
	const proofingSupportedFileTypes = validatedProofingSupportedFileTypes.length > 0 ? validatedProofingSupportedFileTypes.split(",") : [];

	// set custom headers
	const customHeaders = session.CustomRequestHeaders.replace(/\s/g, "");
	const customRequestHeaders = customHeaders.length > 0 ? customHeaders.split(";") : [];

	// save user default nodes - they cannot be processed until reflection model is loaded;
	// TODO: ideally we would separate session init into its own service module and handle loading
	// reflection and user defaults all in one shot
	const userDefaultNodes = response.getNodesOfType("UserDefault");
	
	const userLocale = userDetails.Locale;

	// enable location hash encoding if so configured
	setLocationHashEncodingEnabled(!!session.EncodeURLHashParameters);

	// setup role being played by user and other possible roles
	const roles = rootCache.getNodes(sessionConstants.configEditor ? session.RolesWithManagedPreferences : session.PlayableRoles);
	let urlRolePlayed = user.RolePlayed;

	if (xml.isEmptyURL(urlRolePlayed) && !crews.isAdmin() && roles.length) {
		urlRolePlayed = roles[0].$URL;
	}

	const role = rootCache.get(urlRolePlayed);
	if (role) {
		roleUrl = urlRolePlayed;
		roleName = role.$Name;
	}

	// do not allow searching on breadcrumb for non-host users - could give them access to nodes they are not supposed to see;
	// also, do not allow searching on breadcrumb for users who play role that has Enable Breadcrumb Search disabled
	const enableBreadcrumbSearch = !sessionConstants.isLocalUser && (!role || role.EnableBreadcrumbSearch);

	if (sessionConstants.isPortalUser) { //TA31708: REMOVE the old Web Collection Book and redirect the user to the new collection book
		const collectionbookURL = csi.url("collectionbook");
		//redirect to collection book url, if it is available
		xhr(collectionbookURL)
			.then((response) => {
				document.location.replace(collectionbookURL);
			});
	}

	sessionConstants.initialize({
		admin: bAdmin,
		allow3d: session.Allow3d,
		allow3dAnnotations: session.Allow3dAnnotations,
		clarityId: session.ClarityId,
		configEditor,
		configViewer,
		customRequestHeaders,
		datePattern: localeSetting.DatePattern,
		debugViewer,
		decimalSeparator: localeSetting.DecimalSeparator,
		dev,
		enableBreadcrumbSearch,
		excludedFileExtensions,
		exemptedFileExtensions,
		froalaLicense: session.FroalaLicense,
		fusionChartsLicense: session.FusionChartsLicense,
		groupSeparator: localeSetting.GroupSeparator,
		gtmId: session.GtmId,
		idleExpirationTime: session.IdleExpirationTime,
		isAgent,
		isAsperaEnabled: session.IsAsperaEnabled,
		isDriveEnabled: session.IsDriveEnabled,
		isMSTeamsEnabled: session.IsMSTeamsEnabled,
		isSlackEnabled: session.IsSlackEnabled,
		isTextFlipHorizontalAllowed: session.IsTextFlipHorizontalAllowed,
		keycloakEnabled: session.KeycloakEnabled,
		labelTemplateTypes,
		maxResultSize,
		msTeamsUserId: session.MSTeamsUserId,
		proofingSupportedFileTypes,
		roleName,
		roleUrl,
		serviceAccountAuthenticationTypesEnabled: session.ServiceAccountAuthenticationTypesEnabled,
		sessionUrl: session.$URL,
		setupViewer,
		showUserNameOnHeader,
		siteLocale,
		siteMaxCompositionColumns,
		siteMaxExportSize,
		sitePaperOrientation,
		sitePaperSize,
		slackBotUserId: session.SlackBotUserId,
		slackUserId: session.SlackUserId,
		serverTimeZoneOffset: session.ServerTimeZoneOffset,
		timePattern: localeSetting.TimePattern,
		userAuthenticationTypesEnabled: session.UserAuthenticationTypesEnabled,
		userCompanyUrl,
		userCompanyInfoUrl,
		userDefaultNodes,
		userSupplierUrl,
		userLicense,
		userLocale,
		userType,
		userUrl,
		validIllustratorExtensions,
	});

	sessionStore.setLastVisited(session.LastVisited);
	sessionStore.setPlayableRoles(roles);

	// determine if user's current playable role is still available
	if (!sessionStore.playableRolesStore.get(urlRolePlayed)) {
		urlRolePlayed = null;
	}

	sessionStore.setSkinTheme(userDetails.SkinTheme);
	if (session.IsAdminOnlyMode) {
		sessionStore.setAdminOnly(true);
	}
	// initialize site and role preferences to determine access to views, paths, etc.
	configPreferences.init(sessionStore.sitePreferences, sessionStore.getRolePreferences());
	configPreferences.setUserDefaultValues(sessionStore.userDefaults);

	csi.setConfigurationPrefix(session.GeneratedPrefix);

	// determines if comparison operators are available on certain attributes in custom view
	if (featureTogglesStore.RESTORE_TEXT_COMPARISON_OPERATORS) {
		csiHeading.allowTextComparisonOperators = true;
	}

	// determines if we revert back to using single select for string with multi-line text format and no format
	if (featureTogglesStore.RESTORE_SELECT_INPUT_FOR_EQUALS_OPERATORS) {
		csiHeading.restoreSelectInputForEqualsOperators = true;
	}
}

/**
 * return logo for online user
 */
export function getLogo(bHost?: boolean): cnl {
	const { userCompanyInfoUrl } = sessionConstants;
	return csi.urlForGenerated("Logo" + ((userCompanyInfoUrl && !bHost) ? ("_" + userCompanyInfoUrl) : "") + ".png");
}

/**
 * Return the AutoComplete enabled or not for the current user
 */
export function isAutoCompleteEnabled(): boolean {
	const enableAutoComplete = sessionStore.currentUser.EnableAutoComplete;
	return enableAutoComplete == undefined ? true : !!enableAutoComplete;
}

function _initLocale(): Promise<void> {
	return initLocale(sessionStore.userDetails.Locale);
}

/**
 * Determine if Pinyin support should be enabled for application
 */
function _initPinyin(): void {
	if (LanguageUtil.isChineseLocale(sessionStore.locale)) {
		LanguageUtil.enablePinyin();
	}
}

/**
 * Gets the location of a given search parameter value
 */
function getLocationSearchParameterValue(param: string): string | undefined {
	return new URLSearchParams(location.search)
		.get(param);
}